import React, { useState } from 'react'

import { Button, PriceString, RadioButton, TableUI } from '@fourthwall/components'
import { ModalBox } from '@fourthwall/components/dist/partials/ModalBox'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import * as S from './PackageSizeModal.styled'
import { LABELS } from './labels'
import type { Size } from './types'

export type ProductSize = {
  size: Size
  examples: string[]
  maxSize: {
    dimensions: string
    volume: string
  }
  storage: {
    feePerMonth: number
  }
  inputLabel?: string
}

const columnHelper = createColumnHelper<ProductSize>()

const columns = (checkedValue: string) => [
  columnHelper.accessor('size', {
    id: 'size',
    header: 'Size category',
    cell: (info) => (
      <S.Size>
        <div style={{ textTransform: 'capitalize' }}>
          <RadioButton
            label={<strong>{LABELS[info.getValue() as Size]}</strong>}
            value={info.getValue()}
            checkedValue={checkedValue}
          />
        </div>
        <p>{info.row.original.examples.join(', ')}</p>
      </S.Size>
    ),
  }),
  columnHelper.accessor('maxSize', {
    id: 'maxSize',
    header: 'Max size (L x W x H)',
    cell: (info) => (
      <S.MaxSize>
        <span>
          <strong>{info.getValue().dimensions}</strong>
        </span>
        <div>{info.getValue().volume}</div>
      </S.MaxSize>
    ),
  }),
  columnHelper.accessor('storage', {
    id: 'storage',
    header: 'Storage fee per item',
    cell: (info) => (
      <S.Storage>
        <PriceString value={info.getValue().feePerMonth} minimumFractionDigits={2} /> / month
      </S.Storage>
    ),
  }),
]

export type PackageSizeModalProps = {
  currentValue?: Size
  sizesData: ProductSize[]
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: (value: string) => void
}

export const PackageSizeModal = ({
  sizesData,
  currentValue,
  onClose,
  onConfirm,
}: PackageSizeModalProps) => {
  const [checkedValue, setCheckedValue] = useState<Size>(currentValue || 'SMALL')

  const handleCancelClick = () => {
    onClose?.()
  }

  const handleConfirmClick = () => {
    onClose?.()

    if (onConfirm) {
      onConfirm(checkedValue)
    }
  }

  const table = useReactTable({
    data: sizesData,
    columns: columns(checkedValue),
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <ModalBox
      title="Select product size"
      size="large"
      buttons={[
        <Button label="Cancel" appearance="secondary" onClick={handleCancelClick} />,
        <Button label="Confirm" appearance="primary" onClick={handleConfirmClick} />,
      ]}
      buttonsDirection="row"
      onClose={onClose || handleCancelClick}
    >
      <TableUI.Root hoverable={false} minWidth="600px">
        <TableUI.Head>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableUI.HeadTr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableUI.HeadTh key={header.id} colSpan={header.colSpan} textAlign="left">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableUI.HeadTh>
                  )
                })}
              </TableUI.HeadTr>
            )
          })}
        </TableUI.Head>

        <TableUI.Body>
          {table.getRowModel().rows.map((row) => (
            <TableUI.BodyTr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableUI.BodyTd
                  key={cell.id}
                  verticalAlign="top"
                  onClick={() => {
                    setCheckedValue(cell.row.original.size)
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableUI.BodyTd>
              ))}
            </TableUI.BodyTr>
          ))}
        </TableUI.Body>
      </TableUI.Root>
    </ModalBox>
  )
}
